import React from "react";
import { Box, Tooltip, Typography, useTheme, Avatar } from "@mui/material";
import { ILead } from "../../../redux/leads/leads.types";
import { RightPanel } from "../../../components/RightPanel";
import { ActivityWhatsAppLogs } from "../../Chatbot/ChatCenter/ActivityWhatsAppLog";
import { MODULE_NAMES } from "../../../constants/enums";
import { formatText } from "../../../helpers";
import { Close, RefreshOutlined } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import { WhatsAppMessageTypeToggle } from "../../../components/WhatsAppMessageTypeToggle/WhatsAppMessageTypeToggle";
import { useDispatchWrapper } from "../../../hooks";
import { fetchWhatsAppConversationListAsync } from "../../../redux/activity/activityActions";
import { useBillingCompanyContext } from "../../../providers/BillingCompanyProvider";
import { getWhatsappWebServiceValue } from "../../../services/subcriptionApi";
import { IMarketCampaignLogs } from "../../../redux/marketCampaign/marketCampaign.types";
import { IActionItem } from "../../../redux/actionItems";
import { IUpsellLead } from "../../../redux/upsellLead";
import { WhatsAppServiceToggle } from "../../Chatbot/components/WhatsAppServiceToggle";
import { useWhatsAppServiceToggle } from "../../Chatbot/hooks/useWhatsAppServiceToggle";
import { WhatsappActivationGuard } from "../../Chatbot/ChatCenter/components/WhatsappActivationGuard/WhatsappActivationGuard";
import { useAuth } from "../../../contexts/AuthProvider";
import { isMetaProvider } from "../../../contexts/AuthProvider/AuthProvider.helpers";

export interface IWhatsappDetailsProps {
  open: boolean;
  leadDetails?: ILead | null;
  upsellLeadDetails?: IUpsellLead | null;
  actionItemDetails?: IActionItem | null;
  campaignLogsData?: IMarketCampaignLogs | null;
  onClose: () => void;
  phoneNumber?: string;
  message?: string;
}

export const ViewWhatsappDetails: React.FC<IWhatsappDetailsProps> = (props) => {
  const {
    open,
    leadDetails,
    onClose,
    phoneNumber,
    campaignLogsData,
    upsellLeadDetails,
    actionItemDetails,
    message,
  } = props;

  const theme = useTheme();
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const { handleChange: handleChangeWhatsappService } = useWhatsAppServiceToggle({
    onSuccess: () => handleRefresh(),
  });

  const activityLogsRef = React.useRef<{ handleRefresh: () => void }>(null);
  const [useQRSend, setUseQRSend] = React.useState(false); // Default to Meta (false) when toggle is visible
  const { whatsappService } = useAuth(); // Use centralized context
  const useMetaService = isMetaProvider(whatsappService);

  // Get the correct whatsapp_web_service value for API calls
  const getCorrectWhatsappWebService = (useQRSend: boolean) => {
    return getWhatsappWebServiceValue(useMetaService, useQRSend);
  };



  // Helper to get upsell lead contact info from nested lead_detail
  const getUpsellLeadPhone = () => {
    const leadDetail = upsellLeadDetails?.lead_detail;
    if (!leadDetail?.contact_number_one) return null;

    if (leadDetail.contact_number_one.startsWith("+91")) {
      return leadDetail.contact_number_one;
    }
    return `${leadDetail.country_code || ""}${leadDetail.contact_number_one}`;
  };

  const handleRefresh = () => {
    if (activityLogsRef.current) {
      activityLogsRef.current.handleRefresh();
    }
  };

  const handleToggleChange = (newUseQRSend: boolean) => {
    setUseQRSend(newUseQRSend);

    if (leadDetails?.contact_number_one) {
      const phone =
        phoneNumber ||
        (leadDetails.contact_number_one?.startsWith("+91")
          ? leadDetails.contact_number_one
          : `${leadDetails.country_code ? leadDetails.country_code : ""}${leadDetails.contact_number_one
          }`);

      dispatch(
        fetchWhatsAppConversationListAsync(
          phone,
          1,
          50,
          true,
          companyOutlet.comapny.company_outlet_uuid,
          getCorrectWhatsappWebService(newUseQRSend), // Use the correct value
        ),
      );
    } else if (upsellLeadDetails?.lead_detail?.contact_number_one) {
      const phone = phoneNumber || getUpsellLeadPhone();

      if (phone) {
        dispatch(
          fetchWhatsAppConversationListAsync(
            phone,
            1,
            50,
            true,
            companyOutlet.comapny.company_outlet_uuid,
            getCorrectWhatsappWebService(newUseQRSend), // Use the correct value
          ),
        );
      }
    } else if (actionItemDetails?.mobile_no) {
      const phone = phoneNumber || actionItemDetails.mobile_no;

      dispatch(
        fetchWhatsAppConversationListAsync(
          phone,
          1,
          50,
          true,
          companyOutlet.comapny.company_outlet_uuid,
          getCorrectWhatsappWebService(newUseQRSend), // Use the correct value
        ),
      );
    }
  };

  // Get display name for upsell lead
  const upsellLeadName = upsellLeadDetails?.lead_detail?.name_of_guest;

  // Get display phone for upsell lead
  const upsellLeadPhoneDisplay = upsellLeadDetails?.lead_detail
    ?.contact_number_one
    ? `${upsellLeadDetails.lead_detail.country_code || ""}${upsellLeadDetails.lead_detail.contact_number_one
    }`
    : null;

  const customHeader = (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
      sx={{
        width: "100%",
        bgcolor: "white",
      }}
    >
      <Box
        sx={{
          pl: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Avatar
            sx={{
              backgroundColor: theme.palette.grey[200],
              "&:hover": {
                backgroundColor: theme.palette.grey[300],
              },
              color: theme.palette.primary.main,
              width: 50,
              height: 50,
              fontSize: "1rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <PersonIcon />
          </Avatar>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
            {formatText(
              campaignLogsData?.recipient_name ||
              leadDetails?.name_of_guest ||
              upsellLeadName ||
              actionItemDetails?.name ||
              "Unknown Contact",
            )}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            {leadDetails?.contact_number_one
              ? leadDetails?.contact_number_one
              : upsellLeadPhoneDisplay
                ? upsellLeadPhoneDisplay
                : campaignLogsData?.recipient_number
                  ? campaignLogsData?.recipient_number
                  : actionItemDetails?.mobile_no
                    ? actionItemDetails?.mobile_no
                    : "No phone number"}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <WhatsAppMessageTypeToggle
          useQRSend={useQRSend}
          onToggleChange={handleToggleChange}
          leftText="QR"
          rightText="Meta"
        />

        <WhatsAppServiceToggle
          onChange={handleChangeWhatsappService}
        />

        <Tooltip title="Refresh messages">
          <RefreshOutlined
            sx={{
              color: "primary.main",
              fontSize: "1.5rem",
              "&:hover": {
                color: theme.palette.grey[300],
              },
              cursor: "pointer",
            }}
            onClick={handleRefresh}
          />
        </Tooltip>
        <Close
          sx={{
            color: "primary.main",
            cursor: "pointer",
          }}
          onClick={onClose}
        />
      </Box>
    </Box>
  );

  return (
    <RightPanel
      sx={{
        p: 0,
      }}
      open={open}
      // heading="WhatsApp Conversation"
      customHeading={customHeader}
      width="40%"
      onClose={onClose}
      isWrappedWithForm
      hideScroll={true}
      hideCrossTopButton={true}
    >
      <WhatsappActivationGuard>
        {leadDetails && (
          <ActivityWhatsAppLogs
            ref={activityLogsRef}
            onRefresh={handleRefresh}
            leadData={leadDetails}
            moduleInformation={{
              moduleId: leadDetails?.leads_uuid,
              moduleName: MODULE_NAMES.LEADS,
              subModuleName: MODULE_NAMES.LEADS,
              refColumnName: "leads_uuid",
            }}
            phone={
              phoneNumber
                ? phoneNumber
                : leadDetails.contact_number_one?.startsWith("+91")
                  ? leadDetails.contact_number_one
                  : `${leadDetails.country_code ? leadDetails.country_code : ""}${leadDetails.contact_number_one
                  }`
            }
            customerName={
              leadDetails?.name_of_guest ||
              actionItemDetails?.name ||
              "Unknown Contact"
            }
            useQRSend={useQRSend}
            onQRSendChange={setUseQRSend}
            message={message}
          // documentSendingDisabled={documentSendingDisabled}
          />
        )}

        {actionItemDetails && (
          <ActivityWhatsAppLogs
            ref={activityLogsRef}
            onRefresh={handleRefresh}
            phone={phoneNumber ? phoneNumber : actionItemDetails?.mobile_no}
            customerName={actionItemDetails?.name || "Unknown Contact"}
            useQRSend={useQRSend}
            onQRSendChange={setUseQRSend}
            message={message}
          />
        )}

        {upsellLeadDetails && (
          <ActivityWhatsAppLogs
            ref={activityLogsRef}
            onRefresh={handleRefresh}
            moduleInformation={{
              moduleId: upsellLeadDetails?.leads_uuid || "",
              moduleName: MODULE_NAMES.LEADS,
              subModuleName: MODULE_NAMES.LEADS,
              refColumnName: "leads_uuid",
            }}
            phone={phoneNumber || getUpsellLeadPhone() || ""}
            customerName={upsellLeadName || "Unknown Contact"}
            useQRSend={useQRSend}
            onQRSendChange={setUseQRSend}
            message={message}
          />
        )}

      </WhatsappActivationGuard>
    </RightPanel>
  );
};
